import { Component, OnInit, Inject, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Course, Status, AssetType, Asset, ExperienceType } from '@shared/models';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { filter, catchError } from 'rxjs/operators';
import { CurrentContextService } from '@shared/services/currentContextService';
import { CurrentHubService } from '@shared/services/currentHub.service'
import { UploadTranslationFileConfirmationDialog, TranslationErrorDialog } from './translation-dialog-messages.component';

@Component({
  selector: 'course-edit-dialog',
  templateUrl: './course-edit-dialog.component.html',
  styleUrls: ['./course-edit-dialog.component.scss']
})
export class CourseEditDialogComponent implements OnInit {
  courseForm: FormGroup;
  course: Course;
  fileManager: MatDialogRef<FileManagerComponent>;
  ltiEnabled: boolean;
  isTranslationEnabled: boolean;
  translationFileUploadSuccess = false;
  @ViewChild('fileUpload') fileInput: ElementRef<HTMLElement>;
  constructor(
    public _dialogRef: MatDialogRef<CourseEditDialogComponent>,
    private _courseApiService: CoursesAPIService,
    private _CurrentContextService: CurrentContextService,
    private _dialog: MatDialog,
    private _currentHubService: CurrentHubService,
    @Inject(MAT_DIALOG_DATA) public _data: { course: Course, childrenClass: string }
  ) {
    this.courseForm = new FormGroup({
      title: new FormControl('', { validators: [Validators.required, Validators.maxLength(50)] }),
      description: new FormControl(''),
      postermedia_id: new FormControl(''),
      room_id: new FormControl(''),
      information: new FormGroup({
        about: new FormControl(''),
        links: new FormControl(''),
        backlightEnabled: new FormControl(true),
        backlightActivatedPerDefault: new FormControl(false),
        versioningEnabled: new FormControl(false),
        saveProgressionEnabled: new FormControl(false),
        displayAIAgentSettingsEnabled: new FormControl(false),
        editSkyboxEnabled: new FormControl(false),
        displayPoster: new FormControl(false),
        displayOutline: new FormControl(false),
        displayArrow: new FormControl(false),
        scoringEnabled: new FormControl(false),
        trackingEnabled: new FormControl(false),
        scoreAlertEnabled: new FormControl(false),
        scoreAlertMessage: new FormControl(''),
        ltiGradeReport: new FormControl(''),
        ltiMaxScore: new FormControl(0),
        passingScore: new FormControl(0),
        endScene: new FormControl(''),
        completionAlertEnabled: new FormControl(false),
        completionAlertMessage: new FormControl(''),
        deviceEnabled: new FormGroup({
          desktop: new FormControl(true),
          mobile: new FormControl(true),
          headset: new FormControl(true),
        })
      }),
      status: new FormControl(''),

    });
  }

  ngOnInit(): void {
    if (this._data && this._data.course) {
      this.course = this._data.course;
      if (this.course.information && this.course.information.backlightEnabled === undefined) {
        this.course.information.backlightEnabled = true;
      }
      if (this.course.information && this.course.information.backlightEnabled === false) {
        this.course.information.backlightActivatedPerDefault = false;
      }
      if (this.course.information && this.course.information.deviceEnabled === undefined) {
        this.course.information.deviceEnabled = { desktop: true, mobile: true, headset: true };
      }
      if (this.course.information && this.course.information.displayAIAgentSettingsEnabled === undefined) {
        this.course.information.displayAIAgentSettingsEnabled = true;
      }
      if (this.course.information && this.course.information.editSkyboxEnabled === undefined) {
        this.course.information.editSkyboxEnabled = false;
      }
      this.courseForm.reset(this.course);
    }
    this.ltiEnabled = this._currentHubService.isLTIEnabled();
    this.isTranslationEnabled = this._currentHubService.isTranslationEnabled();
  }

  saveAndCloseDialog(): void {
    this._courseApiService.updateCourse(this._data.course.id, this.courseForm.value, true).subscribe(c => {
      this._dialogRef.close(c);
    });

  }
  onOpenFileManager(): void {
    this.fileManager = this._dialog.open(FileManagerComponent, {
      panelClass: 'editor-theme',
      data: {
        title: 'Poster Image',
        type: AssetType.i2D,
        courseId: this.course?.id
      },
      id: 'file-manager'
    });
    this.fileManager
      .afterClosed()
      .pipe(
        filter(event => event !== undefined)
      )
      .subscribe(val => {
        this.newPoster(val);
      });
  }
  newPoster(asset: Asset): void {
    this.course.postermedia = asset;
    this.courseForm.controls.postermedia_id.setValue(asset.id);
  }
  blockEventAndGoToHelp(event: any, type: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.goToHelp(type);
  }

  getContributionTooltips(): string {
    if (!this.course.is_multi) {
      return '';
    } else {
      return 'Player contribution on AI Character and skybox is disabled on multiplayer experiences';
    }
  }
  canEditAgentSettings(): boolean {
    return !this.course.is_multi;
  }
  canEditSkyboxSettings(): boolean {
    return !this.course.is_multi;
  }
  goToHelp(type: string): void {
    const url = this.getLinkFromType(type);
    window.open(url);
  }
  getLinkFromType(type: string): string {
    switch (type) {
      case 'poster':
        return 'http://help.spaces.wondavr.com/en/articles/2491520-create-and-edit-an-experience';
      case 'score':
        return 'http://help.spaces.wondavr.com/en/articles/2548289-edit-quiz-and-scoring-options';
      case 'lti':
        return 'https://help.spaces.wondavr.com/en/articles/4856159-integration-with-moodle-via-lti';
      case 'display':
        return '';
      case 'backlight':
        return 'https://help.spaces.wondavr.com/en/articles/6104048-backlight-effect';
      case 'versioning':
        return 'https://help.spaces.wondavr.com/en/articles/6803375-how-to-handle-versioning';
      case 'save-user-progress':
        return 'https://help.spaces.wondavr.com/en/articles/6808244-how-to-save-user-progression';
      case 'translation':
        return 'https://help.spaces.wondavr.com/en/articles/6848562-import-export-your-experience-script'
      default:
        return '';
    }
  }

  isVersioningAvailable(): boolean {
    return this._currentHubService.isPaid() && !this.course.is_multi;
  }

  getVersioningTooltipMessage(): string {
    if (this.isVersioningAvailable()) {
      return '';
    } else if (this._currentHubService.isFree()) {
      return 'Upgrade to access this feature';
    } else {
      return 'Switch to single player to enable versioning';
    }
  }
  // Set 100 as default maxScore when the property does not exist
  setMaxScoreDefaultValue(event: any): void {
    const value = this.courseForm['controls'].information.get('ltiMaxScore').value;
    if (event.value === 'scoreBasedGrade' && (value === 0 || value === null)) {
      this.courseForm['controls'].information.get('ltiMaxScore').setValue(100, { emitEvent: false });
    }
  }
  public is360Only(): boolean {
    return this.course && this.course.experience_type === ExperienceType.only360;
  }

  public downloadTranslationFile(): void {
    this._courseApiService.downloadTranslationFile(this.course.id);
  }
  public uploadTranslationFile(): void {
    this.translationFileUploadSuccess = false;
    this._dialog
      .open(UploadTranslationFileConfirmationDialog, {
        width: '450px',
        height: 'auto',
        panelClass: ['editor-theme'],
      })
      .afterClosed()
      .subscribe((val) => {
        if (val === 'ok') {
          let el: HTMLElement = this.fileInput.nativeElement;
          el.click();
        }
      })
  }
  public onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);


      this._courseApiService.uploadTranslationFile(this.course.id, formData)
        .pipe(
          catchError(err => {
            if (err.status === 500) {
              this.translationFileUploadSuccess = false;
              this._dialog
                .open(TranslationErrorDialog, {
                  width: '450px',
                  height: 'auto',
                  panelClass: ['editor-theme'],
                }).afterClosed()
                .subscribe()
            }
            throw err;
          })
        ).subscribe(()=>{
          this.translationFileUploadSuccess = true;
          setTimeout(()=>{
            window.location.reload();
          }, 2500);
        })

    }
  }

}
